import { IconKey, IconSettings, IconUserPlus } from "@tabler/icons-react";

// src/data/commands.ts
import type { Icon } from "@tabler/icons-react";
import type { NavigateFunction } from "react-router-dom"; // Or appropriate type for navigation

// --- Command Definition Interfaces ---
export interface CommandActionContext {
  navigate: NavigateFunction;
  /** Function to trigger opening a specific dialog/modal */
  openDialog?: (dialogId: string) => void;
  // Add other context functions if needed
}

export interface CommandDefinition {
  /** Unique identifier for the command (used for keys, pinning, recents) */
  id: string;

  /** i18n translation key for the command's display name */
  nameKey: string;

  /** Type of command */
  type: 'navigation' | 'action';

  /** Target URL (can exist for both types, used if action isn't prioritized or doesn't exist) */
  url?: string;

  /** Function to execute (required if type is 'action') */
  action?: (context: CommandActionContext) => void;

  /** Icon component to display (optional) */
  icon?: Icon;

  /** Array of pathname strings where this command should be suggested (optional) */
  contexts?: string[]; // e.g., ['/settings/api-keys', '/dashboard']

  /** Additional keywords for searching (optional) */
  keywords?: string[];

  /** If true, command is only available/suggested for admin users (optional) */
  adminOnly?: boolean;

  /** If true and an action exists, execute the action instead of navigating, even if a URL is present. Defaults to false. */
  executeActionFirst?: boolean;
}
// --- End of Interfaces ---


// --- Example Action Implementations ---
// These could be defined elsewhere and imported, or defined inline if simple.
// They need access to the CommandActionContext provided when called.

const navigateToCreateApiKey = (context: CommandActionContext) => {
  console.log("Action: Opening create API key dialog...");
  // Use the openDialog function from the context
  context.openDialog?.('create-api-key');
};

const openGeneralSettings = (context: CommandActionContext) => {
    context.navigate('/settings/profile'); // Example: navigate to profile settings
}

// --- Command List ---
export const actionCommands: CommandDefinition[] = [
  {
    id: "cmd-create-api-key", // Prefix with 'cmd-' to avoid ID clashes with nav items
    nameKey: "commands.apiKeys.create", // e.g., "Create New API Key"
    type: "action", // Still primarily an action
    action: navigateToCreateApiKey,
    url: '/settings/api-keys', // Add the related URL
    executeActionFirst: true, // Prioritize the action (opening dialog)
    icon: IconKey,
    contexts: ["/settings/api-keys"],
    keywords: ["generate", "token", "secret", "new"],
    adminOnly: false, // Assuming all users can create keys
  },
  {
    id: "cmd-invite-user",
    nameKey: "commands.users.invite", // e.g., "Invite New User"
    type: "action",
    action: (context) => { /* Implement invite user action, maybe open dialog */ console.log("Action: Invite User"); },
    icon: IconUserPlus,
    contexts: ["/admin/users"], // Suggest on Users page
    adminOnly: true, // Only admins can invite
    keywords: ["add", "member", "team"],
  },
   {
    id: "cmd-open-settings",
    nameKey: "commands.settings.open", // e.g., "Open Settings"
    type: "action", // Can still have an action, but navigation is default if url exists
    action: openGeneralSettings, // Action might still be useful, e.g., if called directly elsewhere
    url: '/settings/profile', // Add the corresponding URL
    // executeActionFirst defaults to false, so navigation will be prioritized
    icon: IconSettings,
    keywords: ["preferences", "profile", "account"],
    adminOnly: false,
  },
  // ... add more commands as needed
];

// Note: Navigation items from sidebarData could potentially be transformed
// into this CommandDefinition structure as well for a unified command source,
// but for now, we'll keep them separate and merge them in app-sidebar.tsx.